import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDeviceType } from '../../hooks/useDeviceType';
import { X } from 'lucide-react';

/**
 * Modal adaptativo que se ajusta según el tipo de dispositivo
 */
export default function SmartModal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'medium',
  className = "" 
}) {
  const deviceType = useDeviceType();
  const contentRef = useRef(null);
  const contentMaxHeight = deviceType === 'mobile' ? 'calc(100vh - 4.5rem)' : 'calc(100vh - 8rem)';

  // Alinear el scroll al top del contenedor principal para que el modal siempre sea visible
  useEffect(() => {
    if (isOpen) {
      const scrollContainer = document.querySelector('[data-app-scroll-container]');
      const previousOverflow = scrollContainer?.style.overflow;
      const previousOverscroll = scrollContainer?.style.overscrollBehavior;
      const previousBodyOverflow = document.body.style.overflow;
      const previousBodyPosition = document.body.style.position;
      const previousBodyTop = document.body.style.top;
      const previousBodyWidth = document.body.style.width;
      const previousScrollY = window.scrollY;

      if (scrollContainer) {
        scrollContainer.style.overflow = 'hidden';
        scrollContainer.style.overscrollBehavior = 'contain';
      }

      if (deviceType === 'mobile') {
        document.body.style.position = 'fixed';
        document.body.style.top = `-${previousScrollY}px`;
        document.body.style.width = '100%';
      }

      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
      });

      return () => {
        document.body.style.position = previousBodyPosition || '';
        document.body.style.top = previousBodyTop || '';
        document.body.style.width = previousBodyWidth || '';
        document.body.style.overflow = previousBodyOverflow || '';
        if (scrollContainer) {
          scrollContainer.style.overflow = previousOverflow || '';
          scrollContainer.style.overscrollBehavior = previousOverscroll || '';
        }
        if (deviceType === 'mobile') {
          window.scrollTo(0, previousScrollY);
        }
      };
    }
  }, [isOpen, deviceType]);

  if (!isOpen) return null;

  // Tamaños según dispositivo
  const getModalClasses = () => {
    const baseClasses = "bg-white rounded-lg shadow-xl max-h-[95vh] overflow-hidden flex flex-col min-h-0";
    
    switch (deviceType) {
      case 'mobile':
  return `${baseClasses} fixed inset-0 w-full h-full rounded-none max-h-full`;
      
      case 'tablet':
        return `${baseClasses} w-[90vw] max-w-2xl max-h-[90vh]`;
      
      case 'desktop-small':
        const smallSizes = {
          small: 'w-[80vw] max-w-lg',
          medium: 'w-[85vw] max-w-2xl', 
          large: 'w-[90vw] max-w-3xl'
        };
        return `${baseClasses} ${smallSizes[size]} max-h-[85vh]`;
      
      case 'desktop-large':
      default:
        const largeSizes = {
          small: 'w-[60vw] max-w-lg min-w-[500px]',
          medium: 'w-[70vw] max-w-2xl min-w-[700px]', 
          large: 'w-[80vw] max-w-4xl min-w-[900px]'
        };
        return `${baseClasses} ${largeSizes[size]} max-h-[90vh]`;
    }
  };

  const getOverlayClasses = () => {
    if (deviceType === 'mobile') {
      return "fixed inset-0 z-50 bg-white";
    }
    return "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4";
  };

  return createPortal(
    <div className={getOverlayClasses()} onClick={deviceType !== 'mobile' ? onClose : undefined}>
      <div 
        className={`${getModalClasses()} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between border-b border-gray-200 ${
          deviceType === 'mobile' ? 'p-4' : 'p-6'
        }`}>
          <h3 className={`font-semibold text-gray-900 ${
            deviceType === 'mobile' ? 'text-lg' : 'text-xl'
          }`}>
            {title}
          </h3>
          <button
            onClick={onClose}
            className={`text-gray-400 hover:text-gray-600 transition-colors ${
              deviceType === 'mobile' ? 'p-2' : 'p-1'
            }`}
          >
            <X className={deviceType === 'mobile' ? 'h-6 w-6' : 'h-5 w-5'} />
          </button>
        </div>

        {/* Content */}
        <div 
          ref={contentRef}
          className={`overflow-y-auto flex-1 ${
            deviceType === 'mobile' ? 'p-4' : 'p-6'
          }`}
          style={{ WebkitOverflowScrolling: 'touch', maxHeight: contentMaxHeight }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}